import {
  DateRangePreset,
  DateRangeValue,
  getPresetRange,
  isLastNHoursPreset,
  serializePresetToken,
} from '@app/components/date-range-selector/date-range/date-range-presets';
import { useTimezone } from '@app/core/hooks/useTimezone';
import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { useYoutubeStatsEnabled } from '@app/modules/audit/hooks/useYoutubeStatsEnabled';
import { useStreamUserDefinedRange } from '@app/modules/logger/screens/stream/stores/stream-screen.store';
import { useWebAppGetYoutubeIntegrationStatsCountryViewers } from '@mi-front/api-v2';
import { date } from '@mi-front/core';
import { keepPreviousData } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { YoutubeStatsView } from './layout/YoutubeStatsView';
import { useYoutubeStatsCapture, YoutubeStatsRangeLabel } from './useYoutubeStatsCapture';

const DEFAULT_PRESET: DateRangePreset = 'customRange';

/**
 * Converts the logger selected range into selector Date objects.
 * Returns null while the logger range is not ready.
 */
function toDateRangeFromLoggerSelection(
  userRange: ReturnType<typeof useStreamUserDefinedRange>,
): DateRangeValue | null {
  if (userRange.isLoading || userRange.from <= 0 || userRange.to <= 0) {
    return null;
  }

  return {
    from: new Date(userRange.from),
    to: new Date(userRange.to),
  };
}

/**
 * Keeps query range end aligned to "now" for live data windows.
 * For "Custom date" it preserves the selected day as range start and caps end to now.
 */
function toQueryRange(
  value: DateRangeValue,
  preset: DateRangePreset,
): { from: string; to: string } {
  const now = date().startOf('minute').toDate();

  if (preset === 'customSingleDate') {
    return {
      from: value.from.toISOString(),
      to: value.to.toISOString(),
    };
  }

  const toDate = value.to.getTime() > now.getTime() ? now : value.to;

  if (preset === 'fromCustomDateUntilNow') {
    return {
      from: value.from.toISOString(),
      to: now.toISOString(),
    };
  }

  return {
    from: value.from.toISOString(),
    to: toDate.toISOString(),
  };
}

function toCaptureLabel(preset: DateRangePreset): YoutubeStatsRangeLabel {
  if (isLastNHoursPreset(preset)) {
    switch (preset.hours) {
      case 3:
        return '3hrs';
      case 6:
        return '6hrs';
      case 12:
        return '12hrs';
      default:
        return `lastNHours:${preset.hours}`;
    }
  }

  switch (preset) {
    case 'lastHour':
      return '1hr';
    case 'last24Hours':
      return '24hrs';
    case 'today':
      return 'today';
    case 'yesterday':
      return 'yesterday';
    case 'customSingleDate':
      return 'customDate';
    case 'fromCustomDateUntilNow':
      return 'customDate';
    default:
      return serializePresetToken(preset);
  }
}

function areDateRangesEqual(left: DateRangeValue, right: DateRangeValue): boolean {
  return left.from.getTime() === right.from.getTime() && left.to.getTime() === right.to.getTime();
}

/**
 * BFF-backed YouTube country viewers stats: one fetch per station/window; channel scope is applied in the view.
 */
export function YoutubeStatsContainer() {
  const youtubeStatsCapture = useYoutubeStatsCapture();
  const userRange = useStreamUserDefinedRange();
  const timezone = useTimezone();
  const selectedLoggerRange = useMemo(() => {
    return toDateRangeFromLoggerSelection(userRange);
  }, [userRange.from, userRange.isLoading, userRange.to]);
  const fallbackRange = useMemo(() => {
    const range = getPresetRange('lastHour', timezone);
    const now = new Date();

    return range ?? { from: now, to: now };
  }, [timezone]);
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>(DEFAULT_PRESET);
  const [dateRange, setDateRange] = useState<DateRangeValue>(selectedLoggerRange ?? fallbackRange);
  const hasAppliedInitialLoggerRange = useRef(Boolean(selectedLoggerRange));

  useEffect(() => {
    if (hasAppliedInitialLoggerRange.current || !selectedLoggerRange) {
      return;
    }

    setDateRange(selectedLoggerRange);
    setDateRangePreset('customRange');
    hasAppliedInitialLoggerRange.current = true;
  }, [selectedLoggerRange]);

  const queryRange = useMemo(
    () => toQueryRange(dateRange, dateRangePreset),
    [dateRange, dateRangePreset],
  );

  const station = useActiveStation();
  const youtubeStatsEnabled = useYoutubeStatsEnabled(station);

  const handleDateRangeChange = useCallback(
    (range: DateRangeValue, preset: DateRangePreset) => {
      youtubeStatsCapture.dateRangeChanged({
        range_label: toCaptureLabel(preset),
      });
      setDateRange(range);
      setDateRangePreset(preset);
    },
    [youtubeStatsCapture],
  );
  const handleDateRangeReset = useCallback(() => {
    if (!selectedLoggerRange) {
      return;
    }

    const shouldSkipReset =
      dateRangePreset === 'customRange' && areDateRangesEqual(dateRange, selectedLoggerRange);
    if (shouldSkipReset) {
      return;
    }

    youtubeStatsCapture.dateRangeChanged({
      range_label: toCaptureLabel('customRange'),
    });
    setDateRange(selectedLoggerRange);
    setDateRangePreset('customRange');
  }, [dateRange, dateRangePreset, selectedLoggerRange, youtubeStatsCapture]);

  const {
    data: countryStats,
    isLoading,
    isFetching,
  } = useWebAppGetYoutubeIntegrationStatsCountryViewers(
    station.id,
    {
      from: queryRange.from,
      to: queryRange.to,
    },
    {
      query: {
        enabled: youtubeStatsEnabled && Boolean(station.id),
        placeholderData: keepPreviousData,
        /** Keeps data fresh for 5m so focus/reconnect/mount does not refetch constantly. */
        staleTime: 5 * 60 * 1000,
      },
    },
  );

  return (
    <YoutubeStatsView
      stats={countryStats}
      isLoading={isLoading}
      isStatsFetching={isFetching}
      stationYoutubeChannelId={station.youtubeChannelId}
      dateRangePreset={dateRangePreset}
      handleDateRangeChange={handleDateRangeChange}
      handleDateRangeReset={handleDateRangeReset}
      dateRange={dateRange}
      loggerDateRange={selectedLoggerRange}
    />
  );
}
